import { cn } from "@/lib/cn"
import { fmtCompactRelative } from "@/lib/format"
import { ChevronRight, Loader2, Play, RefreshCw } from "lucide-react"
import type { TestLabScenario, TestRunStatus } from "@/types/test_lab"

type Props = {
  scenario: TestLabScenario
  selected: boolean
  onSelect: () => void
  onRun: () => void          // run / re-run, depending on whether latestRunId exists
  busy: boolean              // true when this scenario has an in-flight run start
}

// One row in the scenario list. Status-tinted glyph circle on the left,
// compact scenario copy in the middle, and a tiny run/re-run affordance in
// the upper-right. Selected card gets a soft status glow + border.
//
// Layout is mobile-friendly: meta footer is `flex flex-wrap` so the
// turns/assertions/timestamp tokens wrap on narrow widths instead of
// squashing.
export function ScenarioCard({ scenario, selected, onSelect, onRun, busy }: Props) {
  const status = scenario.latestRunStatus
  const hasRun = scenario.latestRunId != null
  const running = status === "running" || status === "pending"
  const issue = statusIssueText(scenario)
  const displayName = scenario.name || scenario.key
  const hoverText = [displayName, scenario.goal].filter(Boolean).join("\n")

  const handleRun = (e: React.MouseEvent) => {
    e.stopPropagation()
    onRun()
  }

  return (
    <div
      role="button"
      tabIndex={0}
      title={hoverText}
      onClick={onSelect}
      onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); onSelect() } }}
      className={cn(
        "group w-full cursor-pointer border-b border-l-2 px-3 py-2 text-left text-base transition outline-none",
        selected
          ? cardGlowFor(status)
          : "border-l-transparent border-b-border hover:bg-white/[0.025] focus-visible:border-l-brand-red focus-visible:bg-white/[0.025]"
      )}
    >
      <div className="flex items-start gap-3">
        <div className="flex shrink-0 flex-col items-center gap-1 pt-0.5">
          <span className="text-xs font-mono leading-none text-app-subtle tabular-nums">
            #{scenario.id}
          </span>
          <StatusGlyph status={status} hasRun={hasRun} issue={issue} />
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate text-base font-medium leading-6 text-app-text" title={displayName}>
                {displayName}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1.5">
              <StatusPill status={status} hasRun={hasRun} skipReason={scenario.skipReason} issue={issue} />
              {scenario.executable && (
                <RunIconButton
                  hasRun={hasRun}
                  running={running}
                  busy={busy}
                  onRun={handleRun}
                />
              )}
            </div>
          </div>

          {scenario.goal && (
            <p className="mt-0.5 truncate text-sm leading-5 text-app-muted" title={scenario.goal}>
              {scenario.goal}
            </p>
          )}

          <div className="mt-1.5 flex items-center gap-x-3 overflow-hidden text-xs text-app-subtle">
            <span>{scenario.turnsCount} turns</span>
            <span>{scenario.assertionsCount} assertions</span>
            {scenario.latestRunAt && (
              <span className="truncate">{fmtCompactRelative(scenario.latestRunAt)}</span>
            )}
          </div>
        </div>
        <ChevronRight size={17} className="mt-1.5 shrink-0 text-app-muted transition group-hover:translate-x-0.5 group-hover:text-white" />
      </div>
    </div>
  )
}

// Selected-card glow tinted by status — matches the mockup's `shadow-glowRed`
// for failed/errored cases, and a subtle success glow when the selected card
// is green. Falls back to a neutral red glow for never-run / running.
function cardGlowFor(status: TestRunStatus | null): string {
  switch (status) {
    case "passed":
      return "border-l-status-success border-b-border bg-status-success/5"
    case "errored":
      return "border-l-status-warning border-b-border bg-status-warning/5"
    default:
      return "border-l-brand-red border-b-border bg-brand-redSoft/80"
  }
}

function RunIconButton({
  hasRun,
  running,
  busy,
  onRun,
}: {
  hasRun: boolean
  running: boolean
  busy: boolean
  onRun: (e: React.MouseEvent) => void
}) {
  const waiting = busy || running
  const label = waiting ? "Running scenario" : hasRun ? "Run scenario again" : "Run scenario"
  return (
    <button
      type="button"
      onClick={onRun}
      disabled={waiting}
      aria-label={label}
      title={label}
      className="grid h-8 w-8 place-items-center rounded-control border border-border bg-white/[0.04] text-app-muted transition hover:border-brand-red/40 hover:bg-brand-red/10 hover:text-brand-red disabled:cursor-not-allowed disabled:opacity-50"
    >
      {waiting ? (
        <Loader2 size={14} className="animate-spin" />
      ) : hasRun ? (
        <RefreshCw size={14} />
      ) : (
        <Play size={14} />
      )}
    </button>
  )
}

// Status glyph circle. Bold unicode glyph reads cleaner than a 16px lucide
// icon at the small target size in the mockup. Border + soft fill take the
// status color so the row tells you at-a-glance whether it's failed.
function StatusGlyph({ status, hasRun, issue }: { status: TestRunStatus | null; hasRun: boolean; issue: string | null }) {
  const { glyph, classes } = (() => {
    if (!hasRun) return { glyph: "◌", classes: "border-app-muted/40 bg-app-bg text-app-muted" }
    switch (status) {
      case "passed":
        return { glyph: "✓", classes: "border-status-success/60 bg-status-success/10 text-status-success" }
      case "failed":
        return { glyph: "×", classes: "border-status-error/60 bg-brand-redSoft text-status-error" }
      case "errored":
        return { glyph: "!", classes: "border-status-warning/60 bg-status-warning/10 text-status-warning" }
      case "skipped":
        return { glyph: "—", classes: "border-app-muted/40 bg-app-bg text-app-muted" }
      case "running":
      case "pending":
        return { glyph: "•", classes: "border-app-muted/40 bg-app-bg text-app-muted animate-pulse" }
      default:
        return { glyph: "◌", classes: "border-app-muted/40 bg-app-bg text-app-muted" }
    }
  })()
  return (
    <div className={cn(
      "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-sm font-bold leading-none",
      classes,
    )} title={issue ?? undefined}>
      {glyph}
    </div>
  )
}

function StatusPill({ status, hasRun, skipReason, issue }:
  { status: TestRunStatus | null; hasRun: boolean; skipReason: string | null; issue: string | null }) {
  if (skipReason) {
    return (
      <span className="group/status relative shrink-0 rounded-control border border-app-muted/40 px-2.5 py-1 text-xs font-semibold text-app-muted" title={issue ?? skipReason}>
        Manual
        <StatusIssueTooltip issue={issue ?? skipReason} />
      </span>
    )
  }
  if (!hasRun) return null
  if (status === "passed") return null
  const tone = (() => {
    switch (status) {
      case "failed":  return "border-status-error/50 bg-status-error/10 text-status-error"
      case "errored": return "border-status-warning/50 bg-status-warning/10 text-status-warning"
      case "running":
      case "pending": return "border-app-muted/40 bg-app-bg text-app-muted"
      case "skipped": return "border-app-muted/40 bg-app-bg text-app-muted"
      default:        return "border-app-muted/40 bg-app-bg text-app-muted"
    }
  })()
  const label = status === "pending" ? "Queued" : status === "errored" ? "Errored" : status ?? "—"
  return (
    <span className={cn("group/status relative shrink-0 rounded-control border px-2.5 py-1 text-xs font-semibold capitalize", tone)} title={issue ?? undefined}>
      {label}
      <StatusIssueTooltip issue={issue} />
    </span>
  )
}

function StatusIssueTooltip({ issue }: { issue: string | null }) {
  if (!issue) return null

  return (
    <span className="pointer-events-none absolute right-0 top-full z-30 mt-2 hidden w-80 max-w-[80vw] rounded-card border border-border bg-app-panel2 p-3 text-left text-[11px] font-normal leading-4 text-app-text shadow-xl normal-case group-hover/status:block">
      <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-app-muted">Latest issue</span>
      <span className="block max-h-40 overflow-hidden whitespace-pre-wrap break-words">{issue}</span>
    </span>
  )
}

function statusIssueText(scenario: TestLabScenario): string | null {
  if (scenario.skipReason) return scenario.skipReason

  const summary = scenario.latestRunSummary?.trim()
  switch (scenario.latestRunStatus) {
    case "errored":
      return summary ? `Error: ${summary}` : "Error: run stopped before the assertions could complete."
    case "skipped":
      return summary ? `Skipped: ${summary}` : "Skipped before execution."
    case "failed": {
      const failed = scenario.latestRunVerdicts.filter((v) => v.passed === false)
      const lines = []
      if (summary) lines.push(summary)
      failed.slice(0, 3).forEach((v) => {
        const reason = v.reason?.trim()
        lines.push(reason ? `${v.text}: ${reason}` : v.text)
      })
      if (failed.length > 3) lines.push(`+${failed.length - 3} more failed assertion${failed.length - 3 === 1 ? "" : "s"}`)
      return lines.length ? lines.join("\n\n") : "Failed assertions."
    }
    case "pending":
      return "Run is queued."
    case "running":
      return "Run is still executing."
    default:
      return null
  }
}
